import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Result } from '../results/result/result';
import { ResultsFilterPipe } from '../results/results-config/results-filter.pipe';
import { ResultsConfig } from '../results/results-config/results-config';
import { SeasonPointsPipe } from '../season-points.pipe';
import { PositionToPointsPipe } from './position-to-points.pipe';
import { StageStanding } from '../race/stage-standing';
import { IsResultWithinMicrotimeRangePipe } from '../results/result/is-result-within-microtime.pipe';
import { RaceRanking } from '../race/race-ranking/race-ranking';
import { StandingFilterPipe } from './standing-filter.pipe';
import { RaceStanding } from '../race/race-standing';
import { Race } from '../race/race';
import { Ranking } from '../rankings/ranking';
import { RaceService } from '../race/race.service';
import { RaceStagesRecord } from '../race/race-stage/race-stage';

@Injectable({
  providedIn: 'root'
})
export class RaceStandingsService {

  constructor(
    private readonly resultsFilter: ResultsFilterPipe,
    private readonly raceService: RaceService,
    private readonly seasonPointsPipe: SeasonPointsPipe,
    private readonly positionToPointsPipe: PositionToPointsPipe,
    private readonly isResultWithinMicrotimeRangePipe: IsResultWithinMicrotimeRangePipe,
    private readonly standingFilterPipe: StandingFilterPipe
  ) {
  }

  // @TODO Add virtual stage to collect out-of-any-stage results?
  createRaceRanking(race: Race, ranking: Ranking): RaceRanking {

    const rankingRaceStagesRecord = this.getRankingRaceStagesRecord(race.raceStages, ranking);

    const standings$: Observable<RaceStanding[]> = combineLatest([
      race.driversByStartNumber$,
      race.results$
    ]).pipe(
      map(([driversByStartNumber, results]) => {
        const rankingStandingsByStartNumber: Record<string, RaceStanding> = {};

        for (let result of results) {
          for (let stageKey in rankingRaceStagesRecord) {
            if (this.isResultWithinMicrotimeRangePipe.transform(
              result,
              [
                rankingRaceStagesRecord[stageKey].startAfterMicrotime,
                rankingRaceStagesRecord[stageKey].endAtMicrotime
              ],
            )) {
              if (!(result.driverId in rankingStandingsByStartNumber)) {
                if (result.driverId in driversByStartNumber) {
                  const potentialRankingRaceStanding: RaceStanding = {
                    driver: driversByStartNumber[result.driverId],
                    stageStandings: {}
                  };
                  if (this.standingFilterPipe.transform(potentialRankingRaceStanding, ranking.standingsConfig)) {
                    rankingStandingsByStartNumber[result.driverId] = potentialRankingRaceStanding;
                  }
                }
              }
              if (result.driverId in rankingStandingsByStartNumber) {
                if (!(stageKey in rankingStandingsByStartNumber[result.driverId].stageStandings)) {
                  rankingStandingsByStartNumber[result.driverId].stageStandings[stageKey] = {
                    results: [],
                    bestResults: [],
                    bestResultsAverage: 0
                  };
                }
                rankingStandingsByStartNumber[result.driverId].stageStandings[stageKey].results.push(result);
              }
            }
          }
        }

        const rankingRaceStandings: RaceStanding[] = Object.values<RaceStanding>(rankingStandingsByStartNumber);
        // .sort(this.compareStandingsByStartNumber);

        this.recalculateBestLaps(race, rankingRaceStandings);
        this.recalculateRaceStandingStages(race, rankingRaceStandings);
        this.recalculateRaceStandings(rankingRaceStandings);

        return rankingRaceStandings;
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1
      })
    );

    return {
      race,
      ranking,
      rankingRaceStages: rankingRaceStagesRecord,
      standings$
    };
  }

  private getRankingRaceStagesRecord(raceStagesRecord: RaceStagesRecord, ranking: Ranking): RaceStagesRecord {
    const rankingRaceStagesRecord: RaceStagesRecord = {};
    for (let stageKey in raceStagesRecord) {
      if (!ranking.stageKeys?.length || ranking.stageKeys.indexOf(stageKey) >= 0) {
        rankingRaceStagesRecord[stageKey] = raceStagesRecord[stageKey];
      }
    }
    return rankingRaceStagesRecord;
  }

  private extractBestResults(results: Result[], resultsConfig: ResultsConfig): Result[] {
    return this.resultsFilter.transform(results, resultsConfig)
      .sort((a, b) => a.microtime - b.microtime)
      .slice(0, resultsConfig.bestLapsNumber);
  }

  private average(results, n = 3) {
    let i;
    let sum = 0;
    for (i = 0; i < results.length && (n <= 0 || i < n); i++) {
      sum += results[i];
    }
    return i ? (sum / i) : null;
  }

  private recalculateBestLaps(race: Race, rankingRaceStandings: RaceStanding[]) {
    rankingRaceStandings.forEach((rankingRaceStanding: RaceStanding) => {
      for (let stageKey in rankingRaceStanding.stageStandings) {
        rankingRaceStanding.stageStandings[stageKey].bestResults = this.extractBestResults(
          rankingRaceStanding.stageStandings[stageKey].results, race.racePlan.stages[stageKey].resultsConfig
        );
        rankingRaceStanding.stageStandings[stageKey].bestResultsAverage = this.average(rankingRaceStanding.stageStandings[stageKey].bestResults.map(result => result.microtime))
      }
    });
  }

  private recalculateRaceStandingStages(race: Race, raceStandings: RaceStanding[]): void {
    const stageStandingsByStageKey: Record<string, StageStanding[]> = {};

    raceStandings.forEach((raceStanding: RaceStanding) => {
      Object.entries(raceStanding.stageStandings).forEach(([stageKey, raceStandingStage]) => {
        if (!(stageKey in stageStandingsByStageKey)) {
          stageStandingsByStageKey[stageKey] = [];
        }
        stageStandingsByStageKey[stageKey].push(raceStanding.stageStandings[stageKey]);
      });
    });

    for (let stageKey in stageStandingsByStageKey) {
      let position = 0;
      stageStandingsByStageKey[stageKey]
        .sort((a, b) => (a.bestResultsAverage || 0) - (b.bestResultsAverage || 0))
        .forEach((raceStageStanding) => {
          if (
            raceStageStanding.bestResultsAverage
            && raceStageStanding.bestResults.length >= race.racePlan.stages[stageKey].resultsConfig.bestLapsNumber
          ) {
            raceStageStanding.position = ++position;
          } else {
            raceStageStanding.position = 0;
          }
          raceStageStanding.points = this.positionToPointsPipe.transform(
            raceStageStanding.position,
            race.racePlan.stages[stageKey].pointsMap
          );
        });
    }
  }

  private recalculateRaceStandings(raceStandings: RaceStanding[]): void {
    raceStandings.forEach((raceStanding) => {
      raceStanding.points = Object.values(raceStanding.stageStandings).reduce((racePoints, raceStandingStage) => {
        return racePoints + raceStandingStage.points;
      }, 0);
    });
    raceStandings
      .sort((a, b) => {
        if (a.points === b.points) {
          return Math.min(...Object.values(a.stageStandings).map((raceStandingStage) => raceStandingStage.bestResultsAverage))
            - Math.min(...Object.values(b.stageStandings).map((raceStageStanding) => raceStageStanding.bestResultsAverage));
        } else if (+a.points === 0) {
          return 1;
        } else if (+b.points === 0) {
          return -1;
        } else {
          return b.points - a.points;
        }
      })
      .forEach((standing, i) => {
        standing.position = i + 1;
      });
  }

  // @TODO
  recalculateSeasonPoints(raceStandings: RaceStanding[], multiplier?: number): void {
    raceStandings.forEach((seasonStanding, i) => {
      seasonStanding.points = this.seasonPointsPipe.transform(
        seasonStanding.position,
        [],
        multiplier/*,
        seasonStanding.hasRaceBastLap*/ // @TODO Add best lap handling
      );
    });
  }

  private compareStandingsByStartNumber(a: RaceStanding, b: RaceStanding): number {
    return a.driver.startNumber - b.driver.startNumber;
  }
}
